/**
 * Panneau d'alertes automatiques : Findings High, CIS France, retards
 * Actions/CCV, PCED non validées, ARED insuffisantes. Chaque alerte est
 * cliquable et ouvre directement le détail correspondant (page + filtre
 * rapide déjà appliqué).
 */
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Clock, FileWarning, ShieldAlert } from "lucide-react";
import { ALERTS } from "@/constants/testIds";

const ICONS = { critical: ShieldAlert, warning: Clock };
const STYLES = {
  critical: "bg-red-50 border-red-100 text-red-700",
  warning: "bg-amber-50 border-amber-100 text-amber-700",
};

// Destination (page + filtre rapide ?qf=) associée à chaque type d'alerte.
// NOTE : "ared_insufficient" n'a volontairement PAS de `qf` - l'écart ARED
// (objectif - disponibles) est un déficit AGRÉGÉ par site (analyses jamais
// créées), pas un statut au niveau de chaque ligne existante (confirmé par
// inspection : 100% des lignes ARED réelles sont déjà "Validé") - filtrer
// les lignes serait donc toujours vide. On ouvre simplement l'onglet ARED
// pour laisser l'utilisateur consulter le détail par site.
const ALERT_DESTINATIONS = {
  findings_high: { path: "/findings", qf: "high" },
  findings_cis: { path: "/findings", qf: "cis" },
  actions_late: { path: "/actions", qf: "late" },
  ccv_late: { path: "/ccv", qf: "late" },
  pced_pending: { path: "/pced", qf: "pending" },
  ared_insufficient: { path: "/indicateurs", tab: "ared" },
  accidents_critical: { path: "/accidents", qf: "critical" },
  accidents_lti: { path: "/accidents", qf: "lti" },
};

export default function AlertsPanel({ alerts = [] }) {
  const navigate = useNavigate();

  const handleAlertClick = (alert) => {
    const dest = ALERT_DESTINATIONS[alert.type];
    if (!dest) return;
    const params = new URLSearchParams();
    if (dest.qf) params.set("qf", dest.qf);
    if (dest.tab) params.set("tab", dest.tab);
    navigate(`${dest.path}?${params.toString()}`);
  };

  return (
    <Card data-testid={ALERTS.container} className="h-full border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <AlertTriangle className="h-4 w-4 text-red-500" /> Alertes Sécurité
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-2 pt-0 sm:grid-cols-2">
        {alerts.length === 0 && (
          <p className="col-span-2 py-6 text-center text-sm text-slate-400">Aucune alerte active. Excellent travail !</p>
        )}
        {alerts.map((alert) => {
          const Icon = ICONS[alert.severity] || FileWarning;
          const clickable = Boolean(ALERT_DESTINATIONS[alert.type]);
          return (
            <div
              key={alert.type}
              data-testid={`${ALERTS.item}-${alert.type}`}
              onClick={clickable ? () => handleAlertClick(alert) : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => e.key === "Enter" && handleAlertClick(alert) : undefined}
              title={clickable ? "Voir le détail" : undefined}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors duration-200 ${STYLES[alert.severity]} ${clickable ? "cursor-pointer hover:shadow-sm hover:brightness-95" : ""}`}
            >
              <div className="flex items-center gap-2.5">
                <Icon className="h-4 w-4 shrink-0" />
                <span className="font-medium">{alert.label}</span>
              </div>
              <Badge variant="outline" className="border-current bg-white/60 font-bold">
                {alert.count}
              </Badge>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
